// Piezas físicas de los servicios del catálogo: darlas de alta, seguirles
// el estado y reservarlas para un evento.
//
// HUBO AQUÍ UN INVENTARIO DE CONSUMIBLES —existencias, compra, consumo
// imputado por evento, recargas y costo unitario con historial— y se retiró:
// solo era correcto si alguien registraba cada consumo, y un inventario que
// nadie alimenta miente. Comprar el insumo, recargar el tanque o reponer una
// pieza se capturan como gasto (general en Cuentas, o del evento), que es lo
// que los venues hacían de todos modos; `adquisicion` sigue aquí porque
// alimenta la recuperación de la inversión.
//
// El servicio vive en venues/{id}/conceptos/{id} y sus piezas en la
// subcolección `unidades`. El nivel de cada pieza (`nivel`) se mide con la
// escala que define el propio servicio (FRA-351): palabras, porcentaje o
// ninguna — ver utils/catalogo.js.

import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { getUnidadesOcupadas, reservasEnConflicto, disponibilidadPool } from "./unidades";
import { esPool, piezasReservablesDe } from "./catalogo";

// Disponibilidad operativa de una pieza. Esto SÍ aplica igual a todo (a
// diferencia del nivel, que es por servicio).
export const ESTADOS_UNIDAD = ["activo", "mantenimiento", "baja"];

export const ESTADO_UNIDAD_LABELS = {
  activo: "Activo",
  mantenimiento: "En mantenimiento",
  baja: "De baja",
};

const itemRef = (venueId, itemId) =>
  doc(db, "venues", venueId, "conceptos", itemId);





// Alta masiva de piezas. Sin esto la carga inicial de un venue con 20
// calentadores es impracticable. `plantilla` acepta {n} como el número.
// `nivelInicial` es el "lleno" de la escala del servicio (nivelLlenoDe), o
// null si el servicio no mide nivel.
export async function crearUnidadesEnLote({
  venueId,
  itemId,
  cantidad,
  plantilla = "Unidad {n}",
  desde = 1,
  skuPrefijo = "",
  ubicacion = "",
  nivelInicial = null,
  user = null,
}) {
  const n = Number(cantidad) || 0;
  if (!venueId || !itemId || !user?.uid || n <= 0) return [];
  const ref = itemRef(venueId, itemId);
  const unidadesCol = collection(ref, "unidades");
  const batch = writeBatch(db);
  // Se devuelven las unidades completas (no solo ids) para que el caller
  // pueda pintarlas de inmediato sin esperar el snapshot del servidor.
  const creadas = [];
  for (let i = 0; i < n; i++) {
    const num = Number(desde) + i;
    const unidadRef = doc(unidadesCol);
    const data = {
      etiqueta: String(plantilla).replace(/\{n\}/g, String(num)) || `Unidad ${num}`,
      sku: skuPrefijo ? `${skuPrefijo}${num}` : null,
      ubicacion: ubicacion || null,
      fechaAdquisicion: null,
      estado: "activo",
      nivel: nivelInicial ?? null,
      ultimaRecarga: null,
    };
    creadas.push({ id: unidadRef.id, ...data });
    batch.set(unidadRef, {
      ...data,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  }
  await batch.commit();
  return creadas;
}

// --- Recursos asignados a un evento -----------------------------------
//
// `recursosAsignados` vive denormalizado en el doc del evento, igual que
// `proveedoresAsignados`, para que detectar el doble uso de una unidad sea
// un scan en memoria sobre los eventos ya suscritos (ver utils/unidades.js).
// `recursoId` en cada entrada es el id del renglón del catálogo.
//
// BLOQUEO DURO, EN TRANSACCIÓN. La pieza guarda sus propias `reservas`
// (unidades/{id}.reservas: [{eventId, eventoNombre, start, end}]) y se
// escriben en la MISMA transacción que `recursosAsignados`. Así dos owners
// que asignan "Calentador 03" a dos eventos traslapados con segundos de
// diferencia ya no ganan los dos: Firestore reintenta al segundo, que lee la
// reserva del primero y recibe el conflicto. El scan sobre `events` se
// conserva para los eventos anteriores a las reservas.


const eventoRef = (venueId, eventId) =>
  doc(db, "venues", venueId, "calendars", "default", "events", eventId);

const unidadRef = (venueId, itemId, unidadId) =>
  doc(db, "venues", venueId, "conceptos", itemId, "unidades", unidadId);

const conceptoRef = (venueId, itemId) =>
  doc(db, "venues", venueId, "conceptos", itemId);

// Las reservas del modo POR CANTIDAD viven en un solo doc por servicio. Es lo
// que permite el bloqueo duro sin pieza que leer: los dos owners que apartan
// sillas a la vez leen y escriben el MISMO doc dentro de su transacción, así
// que Firestore los serializa y el segundo reintenta viendo lo del primero
// (ver utils/unidades.js, "Piezas por cantidad").
const poolRef = (venueId, itemId) =>
  doc(db, "venues", venueId, "conceptos", itemId, "reservas", "pool");

const aTimestamp = (v) => {
  if (!v) return null;
  if (v instanceof Timestamp) return v;
  if (typeof v?.toDate === "function") return Timestamp.fromDate(v.toDate());
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d) ? null : Timestamp.fromDate(d);
};

const reservasDe = (snap) =>
  snap?.exists() && Array.isArray(snap.data().reservas) ? snap.data().reservas : [];

const sinEvento = (reservas, eventId) =>
  reservas.filter((r) => r?.eventId !== eventId);

// [{ recursoId, unidadId }] de todo lo asignado al evento.
const paresDeEvento = (recursosAsignados) => {
  const pares = [];
  (Array.isArray(recursosAsignados) ? recursosAsignados : []).forEach((r) => {
    (Array.isArray(r?.unidadIds) ? r.unidadIds : []).forEach((unidadId) => {
      if (r?.recursoId && unidadId) pares.push({ recursoId: r.recursoId, unidadId });
    });
  });
  return pares;
};

// Conflictos de un juego de piezas contra [start, end]: primero lo que dicen
// las reservas de las piezas (verdad fresca, leída en la transacción) y
// luego el scan en memoria para los eventos viejos. Un conflicto por pieza.
const detectarConflictos = ({
  pares,
  snapsPorUnidad,
  start,
  end,
  eventId,
  events,
}) => {
  const conflictos = [];
  const vistos = new Set();
  pares.forEach(({ unidadId }) => {
    const snap = snapsPorUnidad[unidadId];
    const choques = reservasEnConflicto({
      reservas: reservasDe(snap),
      start,
      end,
      excludeEventId: eventId,
    });
    if (!choques.length || vistos.has(unidadId)) return;
    vistos.add(unidadId);
    const r = choques[0];
    const evento = (events || []).find((e) => e?.id === r.eventId) || {
      id: r.eventId,
      nombre: r.eventoNombre || "",
    };
    conflictos.push({
      unidadId,
      etiqueta: snap?.exists() ? snap.data().etiqueta || "" : "",
      evento,
    });
  });
  getUnidadesOcupadas({
    start,
    end,
    unidadIds: pares.map((p) => p.unidadId),
    excludeEventId: eventId,
    events,
  }).forEach((c) => {
    if (vistos.has(c.unidadId)) return;
    vistos.add(c.unidadId);
    const snap = snapsPorUnidad[c.unidadId];
    conflictos.push({
      unidadId: c.unidadId,
      etiqueta: snap?.exists() ? snap.data().etiqueta || "" : "",
      evento: c.evento,
    });
  });
  return conflictos;
};

// Cupo del modo por cantidad, dentro de una transacción.
//
// Va partido en LEER / EVALUAR / ESCRIBIR porque Firestore no permite leer
// después de escribir en una transacción: cuando hay varios servicios por
// cantidad en el mismo evento (mover de fecha, cancelar) hay que leerlos todos
// antes de tocar el primero.
const leerCupos = async (tx, venueId, itemIds = []) => {
  const cupos = {};
  for (const itemId of itemIds) {
    if (!itemId || cupos[itemId]) continue;
    const cSnap = await tx.get(conceptoRef(venueId, itemId));
    const pSnap = await tx.get(poolRef(venueId, itemId));
    cupos[itemId] = {
      itemId,
      item: cSnap.exists() ? { id: itemId, ...cSnap.data() } : null,
      pSnap,
      reservas: reservasDe(pSnap),
    };
  }
  return cupos;
};

// ¿Cabe? Puro. `total` es la cifra AUTORITATIVA para este evento (no un
// delta), y el servicio se lee en la propia transacción para no validar contra
// un total que el owner ya cambió en otra pestaña.
const evaluarCupo = (cupo, { eventId, total, start, end, events }) => {
  // Si dejó de administrar cantidades, no hay cupo que cuidar: la cifra se
  // guarda en el evento y ya.
  if (!cupo || !esPool(cupo.item)) return null;
  const estado = disponibilidadPool({
    reservables: piezasReservablesDe(cupo.item),
    reservas: cupo.reservas,
    events,
    conceptoId: cupo.itemId,
    start,
    end,
    excludeEventId: eventId,
  });
  const pedidas = Math.max(0, Math.floor(Number(total) || 0));
  if (pedidas <= estado.disponibles) return null;
  const quienes = estado.eventos
    .slice(0, 3)
    .map((e) => `${e.nombre || "otro evento"} (${e.cantidad})`)
    .join(", ");
  return {
    tipo: "cupo",
    recursoId: cupo.itemId,
    recursoNombre: cupo.item?.nombre || "",
    pedidas,
    disponibles: estado.disponibles,
    reservables: estado.reservables,
    ocupadas: estado.ocupadas,
    eventos: estado.eventos,
    mensaje: `${cupo.item?.nombre || "Este servicio"}: pediste ${pedidas} y quedan ${
      estado.disponibles
    } libres de ${estado.reservables}${quienes ? `. Las tienen ${quienes}` : ""}.`,
  };
};

// Deja la reserva de este evento en el cupo (o la quita si `total` es 0).
const escribirCupo = (tx, cupo, { eventId, eventoNombre, total, start, end }) => {
  if (!cupo || !esPool(cupo.item)) return;
  const pedidas = Math.max(0, Math.floor(Number(total) || 0));
  const otras = sinEvento(cupo.reservas, eventId);
  const nuevas =
    pedidas > 0
      ? [
          ...otras,
          {
            eventId,
            eventoNombre: eventoNombre || "",
            cantidad: pedidas,
            start: aTimestamp(start),
            end: aTimestamp(end || start),
          },
        ]
      : otras;
  // set con merge: el doc del pool puede no existir todavía.
  tx.set(cupo.pSnap.ref, { reservas: nuevas }, { merge: true });
};

// Saca a este evento del cupo. Silencioso si el doc no existe: un servicio de
// piezas identificadas nunca lo tuvo.
const liberarCupoLeido = (tx, cupo, eventId) => {
  if (!cupo?.pSnap?.exists()) return false;
  if (!cupo.reservas.some((r) => r?.eventId === eventId)) return false;
  tx.update(cupo.pSnap.ref, { reservas: sinEvento(cupo.reservas, eventId) });
  return true;
};

// Lee en la transacción los docs de las piezas (una sola vez por pieza).
const leerUnidades = async (tx, venueId, pares) => {
  const snaps = {};
  for (const { recursoId, unidadId } of pares) {
    if (snaps[unidadId]) continue;
    snaps[unidadId] = await tx.get(unidadRef(venueId, recursoId, unidadId));
  }
  return snaps;
};

// Escribe la reserva de este evento en cada pieza (reemplazando la suya si
// ya estaba). Piezas borradas del catálogo se saltan.
const escribirReservas = (tx, { pares, snapsPorUnidad, eventId, eventoNombre, start, end }) => {
  const hechas = new Set();
  pares.forEach(({ unidadId }) => {
    if (hechas.has(unidadId)) return;
    hechas.add(unidadId);
    const snap = snapsPorUnidad[unidadId];
    if (!snap?.exists()) return;
    tx.update(snap.ref, {
      reservas: [
        ...sinEvento(reservasDe(snap), eventId),
        {
          eventId,
          eventoNombre: eventoNombre || "",
          start: aTimestamp(start),
          end: aTimestamp(end || start),
        },
      ],
    });
  });
};

// Asigna un recurso del catálogo (y opcionalmente unidades concretas) a un
// evento.
//
// BLOQUEO DURO: si alguna unidad ya está tomada por un evento que se
// traslapa, la operación no se ejecuta y devuelve los conflictos. La
// validación se repite AQUÍ, no solo en el picker, porque entre que la UI
// pintó las opciones y el owner le dio a guardar, otro evento pudo tomarla.
export async function asignarRecursoAEvento({
  venueId,
  eventId,
  item,
  unidadIds = [],
  cantidad = 0,
  events = [],
  user = null,
}) {
  if (!venueId || !eventId || !item?.id) return { ok: false, conflictos: [] };
  const ref = eventoRef(venueId, eventId);
  const pares = (unidadIds || [])
    .filter(Boolean)
    .map((unidadId) => ({ recursoId: item.id, unidadId }));

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Event not found");
    const data = snap.data();
    const snapsPorUnidad = await leerUnidades(tx, venueId, pares);

    if (pares.length > 0) {
      const conflictos = detectarConflictos({
        pares,
        snapsPorUnidad,
        start: data.startTime,
        end: data.endTime || data.startTime,
        eventId,
        events,
      });
      if (conflictos.length > 0) return { ok: false, conflictos };
    }

    const existentes = Array.isArray(data.recursosAsignados)
      ? data.recursosAsignados
      : [];
    const yaEsta = existentes.find((r) => r.recursoId === item.id);
    // Re-asignar acumula (ver abajo), así que el total que este evento va a
    // tener apartado es lo que ya tenía más lo que se pide. El cupo se valida
    // contra ese total, no contra el delta.
    const totalCantidad =
      (yaEsta ? Number(yaEsta.cantidad) || 0 : 0) + (Number(cantidad) || 0);
    const cupos = totalCantidad > 0 ? await leerCupos(tx, venueId, [item.id]) : {};
    if (totalCantidad > 0) {
      const choque = evaluarCupo(cupos[item.id], {
        eventId,
        total: totalCantidad,
        start: data.startTime,
        end: data.endTime || data.startTime,
        events,
      });
      if (choque) return { ok: false, conflictos: [choque] };
    }
    // Re-asignar ACUMULA en lugar de reemplazar: si en el mismo evento se
    // gastan 2 cargas más, o se suma otra unidad, no hay que quitar el recurso
    // (lo que perdería las unidades ya asignadas) para volver a agregarlo.
    const entrada = {
      recursoId: item.id,
      recursoNombre: item.nombre || "",
      unidadIds: yaEsta
        ? [...new Set([...(yaEsta.unidadIds || []), ...unidadIds])]
        : unidadIds,
      cantidad: totalCantidad,
      assignedAt: yaEsta?.assignedAt || Timestamp.now(),
      assignedBy: yaEsta?.assignedBy || user?.uid || null,
      updatedAt: Timestamp.now(),
    };
    const nuevos = yaEsta
      ? existentes.map((r) => (r.recursoId === item.id ? entrada : r))
      : [...existentes, entrada];

    escribirReservas(tx, {
      pares,
      snapsPorUnidad,
      eventId,
      eventoNombre: data.nombre,
      start: data.startTime,
      end: data.endTime || data.startTime,
    });
    if (totalCantidad > 0) {
      escribirCupo(tx, cupos[item.id], {
        eventId,
        eventoNombre: data.nombre,
        total: totalCantidad,
        start: data.startTime,
        end: data.endTime || data.startTime,
      });
    }
    tx.update(ref, { recursosAsignados: nuevos });
    return { ok: true, conflictos: [] };
  });
}

export async function desasignarRecursoDeEvento({ venueId, eventId, recursoId }) {
  if (!venueId || !eventId || !recursoId) return;
  const ref = eventoRef(venueId, eventId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const existentes = Array.isArray(snap.data().recursosAsignados)
      ? snap.data().recursosAsignados
      : [];
    const propio = existentes.find((r) => r.recursoId === recursoId);
    const pares = paresDeEvento(propio ? [propio] : []);
    const snapsPorUnidad = await leerUnidades(tx, venueId, pares);
    // El cupo se libera siempre que el evento tenga cantidad apartada; un
    // servicio de piezas identificadas no tiene doc de pool que limpiar.
    const cupos =
      Number(propio?.cantidad) > 0 ? await leerCupos(tx, venueId, [recursoId]) : {};
    Object.values(snapsPorUnidad).forEach((uSnap) => {
      if (!uSnap?.exists()) return;
      tx.update(uSnap.ref, { reservas: sinEvento(reservasDe(uSnap), eventId) });
    });
    Object.values(cupos).forEach((cupo) => liberarCupoLeido(tx, cupo, eventId));
    tx.update(ref, {
      // Los costos y movimientos ya registrados se conservan: el recurso sí se
      // usó, aunque deje de estar asignado.
      recursosAsignados: existentes.filter((r) => r.recursoId !== recursoId),
    });
  });
}

// Mover el evento de fecha revalida TODAS sus piezas contra el rango nuevo
// y actualiza sus reservas. Se llama antes de guardar el cambio de fechas:
// con `alConflicto: "bloquear"` (default) no escribe nada y devuelve los
// conflictos para que el caller aborte el guardado; con "retirar" (reactivar
// un evento cancelado) quita del evento las piezas que ya tomó otro, reserva
// el resto y devuelve qué se retiró.
export async function sincronizarReservasDeEvento({
  venueId,
  eventId,
  start,
  end,
  events = [],
  alConflicto = "bloquear",
}) {
  if (!venueId || !eventId) return { ok: true, conflictos: [], retiradas: [] };
  const ref = eventoRef(venueId, eventId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Event not found");
    const data = snap.data();
    const asignados = Array.isArray(data.recursosAsignados) ? data.recursosAsignados : [];
    const pares = paresDeEvento(asignados);
    // Lo apartado por cantidad también se revalida: mover una boda al sábado
    // donde ya se fueron las 240 sillas tiene que avisar, no solo cuando lo
    // apartado son piezas con nombre.
    const conCantidad = asignados.filter((r) => r?.recursoId && Number(r.cantidad) > 0);
    if (!pares.length && !conCantidad.length) {
      return { ok: true, conflictos: [], retiradas: [] };
    }

    const inicio = start || data.startTime;
    const fin = end || data.endTime || inicio;
    const snapsPorUnidad = await leerUnidades(tx, venueId, pares);
    const cupos = await leerCupos(tx, venueId, conCantidad.map((r) => r.recursoId));
    const conflictos = detectarConflictos({
      pares,
      snapsPorUnidad,
      start: inicio,
      end: fin,
      eventId,
      events,
    });
    const choquesCupo = conCantidad
      .map((r) =>
        evaluarCupo(cupos[r.recursoId], {
          eventId,
          total: Number(r.cantidad) || 0,
          start: inicio,
          end: fin,
          events,
        })
      )
      .filter(Boolean);
    const todos = [...conflictos, ...choquesCupo];

    if (todos.length > 0 && alConflicto !== "retirar") {
      return { ok: false, conflictos: todos, retiradas: [] };
    }

    const tomadas = new Set(conflictos.map((c) => c.unidadId));
    const libres = pares.filter((p) => !tomadas.has(p.unidadId));
    escribirReservas(tx, {
      pares: libres,
      snapsPorUnidad,
      eventId,
      eventoNombre: data.nombre,
      start: inicio,
      end: fin,
    });
    // Al reactivar un evento cancelado, lo que ya no cabe se retira del evento
    // igual que una pieza tomada: el resto se reserva en el rango nuevo.
    const sinCupo = new Set(choquesCupo.map((c) => c.recursoId));
    conCantidad.forEach((r) => {
      escribirCupo(tx, cupos[r.recursoId], {
        eventId,
        eventoNombre: data.nombre,
        total: sinCupo.has(r.recursoId) ? 0 : Number(r.cantidad) || 0,
        start: inicio,
        end: fin,
      });
    });
    if (tomadas.size > 0 || sinCupo.size > 0) {
      tx.update(ref, {
        recursosAsignados: asignados.map((r) => ({
          ...r,
          unidadIds: (Array.isArray(r.unidadIds) ? r.unidadIds : []).filter(
            (id) => !tomadas.has(id)
          ),
          cantidad: sinCupo.has(r.recursoId) ? 0 : Number(r.cantidad) || 0,
        })),
      });
    }
    return { ok: true, conflictos: todos, retiradas: todos };
  });
}

// Cancelar o borrar un evento libera sus piezas. Recibe `recursosAsignados`
// (del doc que el caller ya tiene) para no releer el evento — al borrar, el
// doc puede irse en el mismo batch. Devuelve cuántas piezas liberó.
export async function liberarReservasDeEvento({ venueId, eventId, recursosAsignados = [] }) {
  const pares = paresDeEvento(recursosAsignados);
  const conCantidad = (Array.isArray(recursosAsignados) ? recursosAsignados : []).filter(
    (r) => r?.recursoId && Number(r.cantidad) > 0
  );
  if (!venueId || !eventId || (!pares.length && !conCantidad.length)) return 0;
  return runTransaction(db, async (tx) => {
    // Todas las lecturas primero: Firestore no permite leer después de
    // escribir dentro de una transacción.
    const snapsPorUnidad = await leerUnidades(tx, venueId, pares);
    const cupos = await leerCupos(tx, venueId, conCantidad.map((r) => r.recursoId));
    let n = 0;
    Object.values(snapsPorUnidad).forEach((uSnap) => {
      if (!uSnap?.exists()) return;
      const antes = reservasDe(uSnap);
      const despues = sinEvento(antes, eventId);
      if (despues.length === antes.length) return;
      tx.update(uSnap.ref, { reservas: despues });
      n += 1;
    });
    Object.values(cupos).forEach((cupo) => {
      if (liberarCupoLeido(tx, cupo, eventId)) n += 1;
    });
    return n;
  });
}

// Compra del activo: el desembolso que después se busca recuperar. Deja el
// dato en el renglón (para calcular la recuperación) y, si se pide, registra
// el gasto real en Cuentas — porque comprar unas luces SÍ es salida de
// dinero del mes, aunque se rente muchas veces después.
//
// El costo queda marcado con `origen: "adquisicion"` para que la
// recuperación no lo cuente como costo de uso: es justo lo que se recupera.
export async function registrarAdquisicion({
  venueId,
  itemId,
  itemNombre,
  monto,
  fecha,
  notas = null,
  registrarGasto = true,
  estado = "pagado",
  user = null,
}) {
  const importe = Number(monto) || 0;
  if (!venueId || !itemId || !user?.uid || importe <= 0) return null;
  const cuando = fecha || Timestamp.now();
  const ref = itemRef(venueId, itemId);
  const costoRef = doc(collection(db, "venues", venueId, "costos"));

  const batch = writeBatch(db);
  batch.update(ref, {
    adquisicion: {
      monto: importe,
      fecha: cuando,
      notas: notas || null,
      registradoComoGasto: !!registrarGasto,
    },
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  if (registrarGasto) {
    batch.set(costoRef, {
      eventId: null,
      concepto: `Compra de ${itemNombre || "recurso"}`,
      categoria: "Equipo",
      proveedorId: null,
      proveedorNombre: null,
      // Todo costo atribuido a un servicio lo referencia con `conceptoId`
      // (ver utils/cotizacionProveedores.js); con `recursoId` el gasto caía
      // en Cuentas pero no aparecía en la ficha del servicio ni en reportes.
      conceptoId: itemId,
      conceptoNombre: itemNombre || "",
      monto: importe,
      montoOriginal: null,
      fecha: cuando,
      estado,
      fechaPago: estado === "pagado" ? cuando : null,
      origen: "adquisicion",
      metodo: null,
      notas: notas || null,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: null,
      updatedBy: null,
    });
  }
  await batch.commit();
  return { costoId: registrarGasto ? costoRef.id : null, monto: importe };
}

